/**
 * « Tracer un chemin » à partir d'un fichier GPX : on choisit le tracé (s'il y en a plusieurs), on le
 * simplifie avec un curseur (Douglas-Peucker : écart maximal au tracé d'origine, en mètres) en voyant
 * le résultat sur la carte, puis on l'utilise comme un tracé fait à la main.
 * Un GPX de montre ou de téléphone compte souvent un point par seconde : des milliers de points inutiles.
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Layer, Source } from '@vis.gl/react-maplibre'
import { parseGpxForImport } from '@web/features/planning/utils/parseGpxForImport'
import { simplifyWithTolerance } from '@web/features/planning/utils/simplifyGpxPath'
import type { LatLng } from '../../api'
import { formatLength, lengthM } from '../../map/geo'

/** Crans du curseur (m) : fins au début, où l'œil voit la différence. */
export const GPX_TOLERANCES = [0, 1, 2, 3, 5, 8, 10, 15, 20, 30, 50]
const DEFAULT_TOLERANCE_INDEX = 4 // 5 m

export type GpxDraft = {
  fileName: string
  name: string
  segments: [number, number][][]
  segment: number
  toleranceIndex: number
}

/** Lit un fichier GPX (tracés trkseg, sinon routes rte). Erreur lisible si rien d'utilisable. */
export async function readGpxFile(file: File): Promise<GpxDraft> {
  const xml = await file.text()
  const parsed = parseGpxForImport(xml)
  if (parsed.segments.length === 0) throw new Error('no-track')
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  const name = doc.querySelector('trk > name, rte > name, metadata > name')?.textContent?.trim() || file.name.replace(/\.gpx$/i, '')
  // Le plus long d'abord : c'est presque toujours celui qu'on veut.
  const lengths = parsed.segments.map(segLength)
  const longest = lengths.indexOf(Math.max(...lengths))
  return { fileName: file.name, name, segments: parsed.segments, segment: longest, toleranceIndex: DEFAULT_TOLERANCE_INDEX }
}

const toLatLng = (c: [number, number]): LatLng => ({ lat: c[1], lng: c[0] })
const segLength = (s: [number, number][]) => lengthM(s.map(toLatLng))

export function simplifiedOf(draft: GpxDraft): LatLng[] {
  return simplifyWithTolerance(draft.segments[draft.segment], GPX_TOLERANCES[draft.toleranceIndex]).map(toLatLng)
}

type CardProps = { draft: GpxDraft; onChange: (d: GpxDraft) => void; onUse: (points: LatLng[], name: string) => void; onCancel: () => void }

export function GpxImportCard({ draft, onChange, onUse, onCancel }: CardProps) {
  const { t } = useTranslation('gestion')
  const original = draft.segments[draft.segment]
  const simplified = useMemo(() => simplifiedOf(draft), [draft])
  const tolerance = GPX_TOLERANCES[draft.toleranceIndex]

  return (
    <div className="g-gpx" role="dialog" aria-label={t('carte.gpx_titre')}>
      <div className="g-gpx__head">
        <span className="mdi mdi-file-upload-outline" aria-hidden />
        <strong>{t('carte.gpx_titre')}</strong>
        <span className="g-muted g-gpx__file">{draft.fileName}</span>
      </div>
      {draft.segments.length > 1 && (
        <label className="g-field">
          <span className="g-field__label">{t('carte.gpx_trace')}</span>
          <select className="g-input" value={draft.segment} onChange={(e) => onChange({ ...draft, segment: Number(e.target.value) })}>
            {draft.segments.map((s, i) => (
              <option key={i} value={i}>
                {t('carte.gpx_segment', { n: i + 1, length: formatLength(segLength(s)), count: s.length })}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="g-field">
        <span className="g-field__label">
          {t('carte.gpx_simplifier')} : <strong>{tolerance === 0 ? t('carte.gpx_aucune') : t('carte.gpx_ecart', { m: tolerance })}</strong>
        </span>
        <input
          type="range"
          min={0}
          max={GPX_TOLERANCES.length - 1}
          step={1}
          value={draft.toleranceIndex}
          onChange={(e) => onChange({ ...draft, toleranceIndex: Number(e.target.value) })}
        />
        <span className="g-gpx__scale g-muted">
          <span>{t('carte.gpx_fidele')}</span>
          <span>{t('carte.gpx_leger')}</span>
        </span>
      </label>
      <p className="g-gpx__stats">
        {t('carte.gpx_points', { from: original.length.toLocaleString('fr-CA'), to: simplified.length.toLocaleString('fr-CA') })}
        <span className="g-muted"> · {formatLength(lengthM(simplified))}</span>
      </p>
      <p className="g-hint">{t('carte.gpx_aide')}</p>
      <div className="g-editor__buttons">
        <button type="button" className="btn btn--primary" disabled={simplified.length < 2} onClick={() => onUse(simplified, draft.name)}>
          {t('carte.gpx_utiliser')}
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          {t('commun.annuler')}
        </button>
      </div>
    </div>
  )
}

/** Aperçu sur la carte : le GPX d'origine en gris fin, le tracé simplifié et ses points par-dessus. */
export function GpxPreviewLayers({ draft }: { draft: GpxDraft }) {
  const simplified = useMemo(() => simplifiedOf(draft), [draft])
  const original = draft.segments[draft.segment]
  const line = (coords: [number, number][]): GeoJSON.Feature => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } })
  const points: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: simplified.map((p) => ({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [p.lng, p.lat] } })),
  }
  return (
    <>
      <Source id="g-gpx-original" type="geojson" data={line(original)}>
        <Layer id="g-gpx-original" type="line" paint={{ 'line-color': '#616161', 'line-width': 2, 'line-opacity': 0.7 }} />
      </Source>
      <Source id="g-gpx-simplified" type="geojson" data={line(simplified.map((p) => [p.lng, p.lat]))}>
        <Layer id="g-gpx-simplified" type="line" paint={{ 'line-color': '#e65100', 'line-width': 4 }} />
      </Source>
      <Source id="g-gpx-points" type="geojson" data={points}>
        <Layer id="g-gpx-points" type="circle" paint={{ 'circle-radius': 3.5, 'circle-color': '#ffffff', 'circle-stroke-color': '#e65100', 'circle-stroke-width': 2 }} />
      </Source>
    </>
  )
}
